import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import {
  type ChainNamespace,
  ChainNamespaces,
  type Contact,
  detectChainNamespace,
} from "@umami/chains";
import { useDynamicDrawerContext } from "@umami/components";
import { getNetworksForContracts } from "@umami/multisig";
import {
  contactsActions,
  useAppDispatch,
  useAsyncActionHandler,
  useAvailableNetworks,
  useValidateName,
  useValidateNewContactPkhByNamespace,
} from "@umami/state";
import { isValidContractPkh } from "@umami/tezos";
import { CustomError, isMultichainEnabled } from "@umami/utils";
import { type FC, useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";

import { DrawerContentWrapper } from "../DrawerContentWrapper";

/**
 * Modal used for both adding new contacts & editing existing contacts.
 *
 * Contact is checked for having unique name & pkh (among all accounts & other contacts) before being added.
 *
 * @param contact - optional / partial data for creating new contact, or full data for editing existing contact.
 */
export const EditContactMenu: FC<{
  contact?: Contact;
}> = ({ contact }) => {
  const { handleAsyncAction } = useAsyncActionHandler();
  const dispatch = useAppDispatch();
  const { goBack } = useDynamicDrawerContext();
  const availableNetworks = useAvailableNetworks();

  const { control, trigger, handleSubmit, formState, getValues, register, setValue } = useForm<
    Omit<Contact, "chainNamespace"> & { chainNamespace?: ChainNamespace }
  >({
    mode: "onBlur",
    defaultValues: {
      ...contact,
      chainNamespace: isMultichainEnabled() ? contact?.chainNamespace : ChainNamespaces.TEZOS,
    },
  });

  const watchedPkh = useWatch({ control, name: "pkh" });
  const detectedNamespace = useMemo(() => detectChainNamespace(watchedPkh), [watchedPkh]);

  // When editing existing contact, its name & pkh are known and provided to the modal.
  const isEdit = !!(contact?.pkh && contact.name);

  const onSubmitContact = async (newContact: Contact) => {
    await handleAsyncAction(async () => {
      let network: string | undefined;

      if (isValidContractPkh(newContact.pkh)) {
        const contractsWithNetworks = await getNetworksForContracts(availableNetworks, [
          newContact.pkh,
        ]);

        network = contractsWithNetworks.get(newContact.pkh);

        if (!network) {
          throw new CustomError(`Network not found for contract ${newContact.pkh}`);
        }
      }
      dispatch(contactsActions.upsert({ ...newContact, network }));
      goBack();
    });
  };

  const { isValid, errors } = formState;
  const validatePkh = useValidateNewContactPkhByNamespace(detectedNamespace || undefined);
  const validateName = useValidateName(contact?.name);

  useEffect(() => {
    if (getValues("pkh") && detectedNamespace && !isEdit) {
      void trigger("pkh");
    }
  }, [trigger, detectedNamespace, getValues, isEdit, setValue]);

  // Update the chainNamespace when namespace is detected
  useEffect(() => {
    if (detectedNamespace && !isEdit) {
      setValue("chainNamespace", detectedNamespace);
    }
  }, [detectedNamespace, setValue, isEdit]);

  return (
    <DrawerContentWrapper title={isEdit ? "Edit contact" : "Add contact"}>
      <form
        onSubmit={handleSubmit(({ name, pkh, chainNamespace = ChainNamespaces.TEZOS }) =>
          onSubmitContact({ name: name.trim(), pkh, chainNamespace })
        )}
      >
        <VStack gap="30px" marginTop="40px" spacing="0">
          <FormControl isInvalid={!!errors.name}>
            <FormLabel>Name</FormLabel>
            <Input
              type="text"
              {...register("name", {
                required: "Name is required",
                validate: validateName,
              })}
              placeholder="Contact's name"
            />
            {errors.name && (
              <FormErrorMessage data-testid="name-error">{errors.name.message}</FormErrorMessage>
            )}
          </FormControl>
          <FormControl isInvalid={!!errors.pkh}>
            <FormLabel>Address</FormLabel>
            <Textarea
              resize="none"
              {...register("pkh", {
                required: "Address is required",
                validate: isEdit ? () => true : validatePkh,
              })}
              disabled={isEdit}
              placeholder="Enter contact's address"
              variant={isEdit ? "filled" : undefined}
            />
            {errors.pkh && (
              <FormErrorMessage data-testid="address-error">{errors.pkh.message}</FormErrorMessage>
            )}
          </FormControl>
        </VStack>
        <Button
          width="full"
          marginTop="30px"
          data-testid="confirmation-button"
          isDisabled={!isValid}
          size="lg"
          type="submit"
          variant="primary"
        >
          {isEdit ? "Update" : "Add contact"}
        </Button>
      </form>
    </DrawerContentWrapper>
  );
};
